import React, { useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { Label } from '@/_components';
import { useGetCurrentUser } from '@/_pages/auth/api';
import { useTheme } from '@/_shared';

import { heightWithLabel, minHeight } from './constants';
import { RichTextLoader } from './loader';
import { Tinymce, type TinymceProps } from './tinymce';

import { useCreateFileMutation } from '../media';

export type RichTextProps = Omit<TinymceProps, 'onLoadContent' | 'height' | 'connectedApiKey'> & {
  label?: string;
  height?: number;
  min_height?: number;
  max_height?: number;
  className?: string;
  mediaFolderPath?: string;
  mediaAccessLevel?: string;
  messengerMode?: boolean;
  onLoadContent?: TinymceProps['onLoadContent'];
  onFocus?: TinymceProps['onFocus'];
  autoFocus?: boolean;
};

export function RichText({
  label,
  className,
  height: _height,
  onLoadContent,
  mediaFolderPath = '',
  mediaAccessLevel = 'private',
  messengerMode = false,
  inline = false,
  autoFocus = false,
  ...editorProps
}: RichTextProps) {
  const { data: currentUser } = useGetCurrentUser();

  let height = _height;

  if (!height) {
    height = label ? heightWithLabel : minHeight;
  }

  const [, setRerenderCount] = useState(0);
  const [isLoading, setLoading] = React.useState(true);
  const { mutateAsync: uploadFile, isPending: isUploadingFile } = useCreateFileMutation();
  const { mode } = useTheme();

  const fileCallback = React.useCallback(
    async (file: File) => {
      try {
        const res = await uploadFile({
          name: file.name,
          access_level: mediaAccessLevel,
          folder_path: `${mediaFolderPath ? `/${mediaFolderPath}` : ''}`,
          media: file,
        });

        return res.data.url;
      } catch (error) {
        console.error('Unable to upload file.', error);
      }
      return null;
    },
    [mediaFolderPath, mediaAccessLevel, uploadFile]
  );

  const handleLoadContent: TinymceProps['onLoadContent'] = (event, editor, ...args) => {
    onLoadContent?.(event, editor, ...args);
    setLoading(false);
    // Hack to fix the issue with tinymce not rendering properly
    setRerenderCount((prev) => prev + 1);

    // If autoFocus is enabled, position cursor at the end of content
    if (autoFocus) {
      // Small delay to ensure content is fully loaded
      setTimeout(() => {
        editor.focus();
        const body = editor.getBody();
        if (body.lastChild) {
          // Try to select the last text node or element
          const lastChild = body.lastChild;
          if (lastChild.nodeType === Node.TEXT_NODE) {
            editor.selection.setCursorLocation(lastChild, lastChild.textContent?.length || 0);
          } else {
            // For element nodes, try to select after the last child
            const range = document.createRange();
            range.setStartAfter(lastChild);
            range.collapse(true);
            editor.selection.setRng(range);
          }
        } else {
          // If no content, just focus at the beginning
          editor.selection.setCursorLocation(body, 0);
        }
      }, 50);
    }
  };

  return (
    <div className={className}>
      <div
        className={twMerge(
          'flex flex-col gap-2',
          isLoading ? 'hidden' : '',
          inline && 'inline-editor',
          messengerMode && 'h-full'
        )}
      >
        {label && <Label>{label}</Label>}
        <Tinymce
          key={`${mode}${inline ? '-inline' : ''}`}
          {...editorProps}
          height={height}
          isUploadingFile={isUploadingFile}
          onFileChange={fileCallback}
          onLoadContent={handleLoadContent}
          inline={inline}
          messengerMode={messengerMode}
          autoFocus={autoFocus}
          connectedApiKey={currentUser?.api_keys?.tiny_mce || ''}
        />
      </div>
      {isLoading && <RichTextLoader withLabel={!!label} height={height} inline={inline} />}
    </div>
  );
}
